import React, { useCallback, useEffect, useState } from 'react';
import {
  adminCreateGroup, adminRenameGroup, adminDeleteGroup, adminUpdateCompany, adminGetStats,
} from '../../api/admin';
import type { CompanyStat } from '../../api/admin';
import type { Company, StatusGroup } from '../../types/auth';
import { useToastStore } from '../../store/useToastStore';
import { ConfirmDialog } from './shared';

// Eigener Menüpunkt „Gruppen": Status-Gruppen verwalten und Mitglieder direkt von
// der Gruppe aus zuweisen/entfernen (statt über jeden Unternehmens-Editor einzeln).

interface Props {
  groups: StatusGroup[];
  companies: Company[];
  onChanged: () => Promise<void>;
}

type PendingMove =
  | { kind: 'add'; company: Company; group: StatusGroup; fromGroup: StatusGroup | null }
  | { kind: 'remove'; company: Company; group: StatusGroup };

export default function GroupsTab({ groups, companies, onChanged }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<StatusGroup | null>(null);
  const [stats, setStats] = useState<CompanyStat[]>([]);

  const loadStats = useCallback(() => {
    adminGetStats().then(setStats).catch(() => undefined);
  }, []);
  useEffect(() => { loadStats(); }, [loadStats, groups, companies]);

  const members = (g: StatusGroup) => companies.filter((c) => c.groupId === g.id);
  const groupStat = (g: StatusGroup) => stats.find((s) => s.companyId === g.id);

  async function doCreate() {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await adminCreateGroup(newName.trim());
      setNewName('');
      addToast('Gruppe erstellt.');
      await onChanged();
    } catch { addToast('Fehler beim Erstellen der Gruppe.', 'danger'); }
    finally { setBusy(false); }
  }

  async function doRename(id: string) {
    if (!renameValue.trim()) { setRenamingId(null); return; }
    setBusy(true);
    try {
      await adminRenameGroup(id, renameValue.trim());
      setRenamingId(null);
      addToast('Gruppe umbenannt.');
      await onChanged();
    } catch { addToast('Fehler beim Umbenennen.', 'danger'); }
    finally { setBusy(false); }
  }

  async function doDeleteGroup(g: StatusGroup) {
    setBusy(true);
    try {
      await adminDeleteGroup(g.id);
      addToast('Gruppe gelöscht.');
      setConfirmDeleteGroup(null);
      await onChanged();
    } catch { addToast('Gruppe konnte nicht gelöscht werden.', 'danger'); }
    finally { setBusy(false); }
  }

  async function applyMove(move: PendingMove) {
    setBusy(true);
    try {
      await adminUpdateCompany(move.company.id, { groupId: move.kind === 'add' ? move.group.id : null });
      addToast(move.kind === 'add'
        ? `„${move.company.name}" zur Gruppe „${move.group.name}" hinzugefügt.`
        : `„${move.company.name}" aus „${move.group.name}" entfernt.`);
      setPending(null);
      await onChanged();
    } catch { addToast('Fehler beim Zuweisen.', 'danger'); }
    finally { setBusy(false); }
  }

  function requestAdd(g: StatusGroup, companyId: string) {
    const company = companies.find((c) => c.id === companyId);
    if (!company) return;
    const fromGroup = company.groupId ? groups.find((x) => x.id === company.groupId) ?? null : null;
    setPending({ kind: 'add', company, group: g, fromGroup });
  }

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4 flex-wrap">
        <div style={{ maxWidth: 560 }}>
          <h6 className="fw-bold mb-1">Status-Gruppen</h6>
          <p className="text-muted small mb-0">
            Unternehmen derselben Gruppe teilen Notizen &amp; Rückruf-Status in Echtzeit —
            wer zuerst zurückruft, markiert für alle. Die Anruf-Filter bleiben pro Unternehmen.
            Beim Beitritt werden vorhandene Einträge des Unternehmens in die Gruppe übernommen
            (pro Anruf gewinnt der neuere Stand).
          </p>
        </div>
        <div className="d-flex gap-2">
          <input
            className="form-control form-control-sm"
            style={{ width: 220 }}
            placeholder="Name der neuen Gruppe…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doCreate(); } }}
          />
          <button className="btn btn-primary btn-sm text-nowrap" disabled={busy || !newName.trim()} onClick={doCreate}>
            + Gruppe anlegen
          </button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-center text-muted py-5">
          <p className="mb-1 fs-5">Noch keine Gruppen</p>
          <p className="mb-0 small">Gruppe anlegen und anschließend Unternehmen hinzufügen.</p>
        </div>
      ) : (
        <div className="row g-3">
          {groups.map((g) => {
            const mem = members(g);
            const st = groupStat(g);
            const addable = companies.filter((c) => c.groupId !== g.id);
            return (
              <div key={g.id} className="col-12 col-md-6 col-xxl-4">
                <div className="card h-100 border shadow-sm rounded-3">
                  <div className="card-header bg-body d-flex align-items-center gap-2 py-2">
                    <span aria-hidden>🔗</span>
                    {renamingId === g.id ? (
                      <input
                        className="form-control form-control-sm"
                        value={renameValue}
                        autoFocus
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); doRename(g.id); }
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        onBlur={() => doRename(g.id)}
                      />
                    ) : (
                      <button
                        className="btn btn-link p-0 fw-semibold text-decoration-none text-body flex-grow-1 text-start"
                        title="Klicken zum Umbenennen"
                        onClick={() => { setRenamingId(g.id); setRenameValue(g.name); }}
                      >
                        {g.name} <span className="text-muted" style={{ fontSize: '0.7rem' }}>✎</span>
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger py-0"
                      style={{ fontSize: '0.72rem' }}
                      disabled={busy || mem.length > 0}
                      title={mem.length > 0 ? 'Erst alle Unternehmen entfernen' : 'Gruppe löschen'}
                      onClick={() => setConfirmDeleteGroup(g)}
                    >
                      Löschen
                    </button>
                  </div>

                  <div className="card-body py-3">
                    <div className="text-muted mb-2" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Mitglieder ({mem.length})
                    </div>
                    {mem.length === 0 ? (
                      <div className="text-muted small mb-2">Noch keine Unternehmen zugewiesen.</div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {mem.map((c) => (
                          <span key={c.id} className="badge bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                            {c.name}
                            <button
                              className="btn btn-link p-0 text-danger text-decoration-none"
                              style={{ fontSize: '0.85rem', lineHeight: 1 }}
                              title="Aus der Gruppe entfernen"
                              disabled={busy}
                              onClick={() => setPending({ kind: 'remove', company: c, group: g })}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <select
                      className="form-select form-select-sm"
                      value=""
                      disabled={busy || addable.length === 0}
                      onChange={(e) => { if (e.target.value) requestAdd(g, e.target.value); e.target.value = ''; }}
                    >
                      <option value="">
                        {addable.length === 0 ? 'Alle Unternehmen sind bereits Mitglied' : '+ Unternehmen hinzufügen…'}
                      </option>
                      {addable.map((c) => {
                        const other = c.groupId ? groups.find((x) => x.id === c.groupId) : null;
                        return <option key={c.id} value={c.id}>{c.name}{other ? ` (aktuell: ${other.name})` : ''}</option>;
                      })}
                    </select>
                  </div>

                  <div className="card-footer bg-body py-2 d-flex gap-2 flex-wrap" style={{ fontSize: '0.75rem' }}>
                    {st ? (
                      <>
                        <span className="badge bg-secondary-subtle text-secondary-emphasis">{st.totalTracked} geteilte Einträge</span>
                        <span className="badge bg-success-subtle text-success-emphasis">{st.done} erledigt</span>
                        <span className="badge bg-warning-subtle text-warning-emphasis">{st.pending} ausstehend</span>
                      </>
                    ) : (
                      <span className="text-muted">Noch keine geteilten Einträge.</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pending?.kind === 'add' && (
        <ConfirmDialog
          title={`„${pending.company.name}" hinzufügen?`}
          danger={false}
          confirmLabel="Hinzufügen"
          busy={busy}
          text={
            <>
              Das Unternehmen teilt dann Notizen &amp; Rückruf-Status mit „{pending.group.name}".
              Vorhandene eigene Einträge werden in die Gruppe übernommen.
              {pending.fromGroup && (
                <><br /><span className="text-warning">Es wird dabei aus „{pending.fromGroup.name}" entfernt; dort geteilte Einträge verbleiben in der alten Gruppe.</span></>
              )}
            </>
          }
          onConfirm={() => applyMove(pending)}
          onCancel={() => setPending(null)}
        />
      )}
      {pending?.kind === 'remove' && (
        <ConfirmDialog
          title={`„${pending.company.name}" entfernen?`}
          confirmLabel="Entfernen"
          busy={busy}
          text={
            <>
              Das Unternehmen sieht die geteilten Notizen &amp; Status der Gruppe danach nicht mehr
              und startet mit einem leeren eigenen Bestand. Die geteilten Einträge bleiben in der Gruppe.
            </>
          }
          onConfirm={() => applyMove(pending)}
          onCancel={() => setPending(null)}
        />
      )}
      {confirmDeleteGroup && (
        <ConfirmDialog
          title="Gruppe löschen?"
          text={`„${confirmDeleteGroup.name}" wird gelöscht.`}
          busy={busy}
          onConfirm={() => doDeleteGroup(confirmDeleteGroup)}
          onCancel={() => setConfirmDeleteGroup(null)}
        />
      )}
    </>
  );
}
